import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Smartphone, Menu as MenuIcon, X, ArrowLeft } from "lucide-react";
import { useI18n } from "../i18n";
import { useCart } from "../context/CartContext";
import LanguageToggle from "./LanguageToggle";
import InstallButton from "./InstallButton";
import { LOGO } from "../lib/site";

export default function Header() {
  const { t } = useI18n();
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setMobile(false), [loc.pathname]);

  const links = [
    { to: "/", label: t.nav.home, id: "home" },
    { to: "/menu", label: t.nav.menu, id: "menu" },
    { to: "/carta", label: t.nav.carta, id: "carta" },
    { to: "/about", label: t.nav.about, id: "about" },
    { to: "/gallery", label: t.nav.gallery, id: "gallery" },
    { to: "/events", label: t.nav.events, id: "events" },
    { to: "/contact", label: t.nav.contact, id: "contact" },
  ];

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#0A192F]/95 backdrop-blur-md border-b border-amber-500/20 py-2" : "bg-[#0A192F]/40 backdrop-blur-sm py-3"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 shrink-0">
          {loc.pathname !== "/" && (
            <button onClick={() => navigate(-1)} data-testid="back-button" aria-label="Back"
              className="p-2 rounded-full hover:bg-amber-500/10 text-slate-100 transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo">
            <img src={LOGO} alt="Restaurante El Timón San Juan del Sur" className="h-12 w-12 rounded-full object-contain bg-white p-1 border border-amber-500/50 shadow-md shrink-0" />
            <div className="hidden sm:block leading-tight">
              <img src="/wordmark.png" alt="El Timón" className="h-6 w-auto" />
              <div className="text-[10px] tracking-[0.18em] text-slate-400 uppercase mt-0.5">San Juan del Sur</div>
            </div>
          </Link>
        </div>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <Link key={l.id} to={l.to} data-testid={`nav-link-${l.id}`}
              className={`font-sub text-sm link-underline transition-colors ${loc.pathname === l.to ? "text-amber-400" : "text-slate-200 hover:text-amber-300"}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden md:block"><InstallButton /></div>
          <LanguageToggle />
          <button data-testid="cart-drawer-toggle" onClick={() => setOpen(true)}
            className="relative p-2 rounded-full hover:bg-amber-500/10 text-slate-100 transition-colors">
            <Smartphone className="h-5 w-5" />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 bg-amber-500 text-slate-950 text-[10px] font-bold h-5 w-5 rounded-full flex items-center justify-center">{count}</span>
            )}
          </button>
          <button onClick={() => navigate("/menu")} data-testid="header-order-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm px-4 py-2 rounded-full transition-all hover:scale-[1.03]">
            <Smartphone className="h-4 w-4" /> {t.common.orderNow}
          </button>
          <button className="lg:hidden p-2 text-slate-100" onClick={() => setMobile((m) => !m)} data-testid="mobile-menu-toggle">
            {mobile ? <X className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="lg:hidden bg-[#0A192F]/98 backdrop-blur-md border-t border-amber-500/20 px-6 py-4 flex flex-col gap-3">
          {links.map((l) => (
            <Link key={l.id} to={l.to} data-testid={`mobile-nav-${l.id}`} className="font-sub text-slate-200 hover:text-amber-300 py-1">{l.label}</Link>
          ))}
          <div className="pt-2"><InstallButton /></div>
          <button onClick={() => { setMobile(false); navigate("/menu"); }} data-testid="mobile-order-btn"
            className="mt-2 inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm px-4 py-3 rounded-full transition-all">
            <Smartphone className="h-4 w-4" /> {t.common.orderNow}
          </button>
        </div>
      )}
    </header>
  );
}
